/**
 * PRESET — "apple-dark"  (ACTIVE)
 * ==============================
 *
 * Dark mode rebuilt on Apple's published Dark Mode system colours; light mode
 * and the accent are carried over from `navy` unchanged.
 *
 * ── What actually changed, and what deliberately did not ────────────────────
 *
 * CHANGED — the dark elevation ramp only:
 *   systemBackground            #000000  app base
 *   secondarySystemBackground   #1C1C1E  cards
 *   tertiarySystemBackground    #2C2C2E  elevated / inputs
 *   opaqueSeparator             #38383A  borders
 *   systemGray3/2/…             #48484A …  strong borders, disabled, tints
 *
 * UNCHANGED — accent, text and feedback. The text ramp was chosen for contrast
 * rather than for the navy behind it, so it improves on black rather than
 * needing a retune: primary 14.04 → 15.63:1 on a card, secondary 8.46 → 9.42,
 * muted 5.54 → 6.16. The Apple-orange accent goes 6.95 → 7.74:1, crossing AA
 * into AAA — and it carries every primary action, active tab and focus ring in
 * the product.
 *
 * Light mode is untouched: it was already built on Apple neutrals.
 */

import navy from './navy';

const appleDark = {
	name: 'apple-dark',
	label: 'Apple Dark',

	// Apple Dark Mode system colours. Ordering matches the navy ramp so every
	// existing `navy.700`-style call-site keeps its intended elevation.
	surfaceScale: {
		900: '#000000', // systemBackground — app base
		// The dark value of `bg.subtle` — an inset step between base and card.
		// Must stay strictly between 900 and 800 or dark-mode elevation flattens.
		850: '#141416',
		800: '#1C1C1E', // secondarySystemBackground — cards (systemGray6)
		700: '#2C2C2E', // tertiarySystemBackground — elevated / hover / inputs (systemGray5)
		600: '#38383A', // opaqueSeparator — borders / dividers
		500: '#3A3A3C', // systemGray4 — control outlines / strong fills
		400: '#48484A', // systemGray3 — strong borders
		300: '#636366', // systemGray2 — heavier dividers
		200: '#8E8E93', // systemGray — disabled foreground, placeholders
		100: '#AEAEB2', // light tint
		50: '#F2F2F7', // near-white (systemGray6 light)
	},

	// Both carried over verbatim — see the header note.
	paper: { ...navy.paper },
	ink: { ...navy.ink },
	accent: { ...navy.accent },
	accentScale: { ...navy.accentScale },
	feedback: { ...navy.feedback },
};

export default appleDark;
